import { RefObject, useCallback, useRef, useState } from 'react';

export type ChartContainer = {
    /** Goes on the container element's `ref`. */
    setContainer: (element: HTMLElement | null) => void;
    /** The same element, for `useElementSize`, which measures through a reference. */
    containerRef: RefObject<HTMLElement>;
    /** The element a chart can be drawn into, or `null` while it is not in the DOM. */
    containerEl: HTMLElement | null;
};

/**
 * The element a chart is drawn into, tracked as state so that a render can react to it.
 *
 * A collapsed section unmounts its container and takes the chart inside it along, while the component
 * holding the draw effect stays mounted. Naming `containerEl` in that effect's dependencies is what
 * rebuilds the chart into the element that is on screen once the section is open again.
 */
export const useChartContainer = (): ChartContainer => {
    const containerRef = useRef<HTMLElement | null>(null);
    const [containerEl, setContainerEl] = useState<HTMLElement | null>(null);

    const setContainer = useCallback((element: HTMLElement | null) => {
        containerRef.current = element;
        setContainerEl(element);
    }, []);

    return { setContainer, containerRef, containerEl };
};
